import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ChevronRight, Search, Users } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useClients } from "@/hooks/use-clients";
import { regionMatches, regionOfClient, useRegionResolver } from "@/hooks/use-regions";
import { usePagination } from "@/hooks/use-pagination";
import { formatDate, formatNumber, formatPhone, matches } from "@/lib/format";
import { formatRegion, type Region } from "@/lib/geo";

export default function Clients() {
  const [search, setSearch] = useState("");
  const [state, setState] = useState("todos");
  const clients = useClients();
  const resolver = useRegionResolver();

  // O estado e a cidade saem da coordenada do endereço padrão, e não do texto
  // digitado: "MG" e "Minas Gerais" deixam de ser dois estados. Ver lib/geo.ts.
  const regions = useMemo(() => {
    const map = new Map<string, Region | null>();
    for (const client of clients.data ?? []) map.set(client.id, regionOfClient(client, resolver.data));
    return map;
  }, [clients.data, resolver.data]);

  const states = useMemo(() => {
    const set = new Set<string>();
    for (const region of regions.values()) if (region?.uf) set.add(region.uf);
    return [...set].sort();
  }, [regions]);

  const rows = useMemo(() => {
    return (clients.data ?? []).filter((client) => {
      if (
        search &&
        !matches(client.name, search) &&
        !matches(client.email, search) &&
        !matches(client.phone, search) &&
        !matches(client.cpf, search) &&
        !regionMatches(regions.get(client.id) ?? null, search)
      ) {
        return false;
      }
      if (state !== "todos") {
        return regions.get(client.id)?.uf === state;
      }
      return true;
    });
  }, [clients.data, search, state, regions]);
  const pagination = usePagination(rows, `${search}|${state}`);

  return (
    <>
      <PageHeader
        title="Clientes"
        description={
          clients.data
            ? `${formatNumber(clients.data.length)} contas cadastradas. Quem usa o aplicativo como convidado não tem conta e não aparece aqui.`
            : "Contas cadastradas no aplicativo do cliente."
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail, telefone, CPF ou cidade"
            className="pl-9"
            aria-label="Buscar cliente"
          />
        </div>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-full sm:w-40" aria-label="Filtrar por estado">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os estados</SelectItem>
            {states.map((uf) => (
              <SelectItem key={uf} value={uf}>
                {uf}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {clients.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Users}
              title={search || state !== "todos" ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
              description={
                search || state !== "todos"
                  ? "Ajuste a busca ou o filtro para ver outros resultados."
                  : "As contas aparecem aqui assim que alguém se cadastra no aplicativo do cliente."
              }
            />
          ) : (
            <Table className="table-stacked">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="hidden md:table-cell">Telefone</TableHead>
                  <TableHead className="hidden lg:table-cell">Cidade</TableHead>
                  <TableHead className="hidden lg:table-cell">Desde</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((client) => {
                  const region = regions.get(client.id);
                  return (
                    <TableRow key={client.id}>
                      <TableCell className="cell-title">
                        <Link
                          href={`/clientes/${client.id}`}
                          className="font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {client.name || "Sem nome"}
                        </Link>
                        <p className="truncate text-caption text-muted-foreground">
                          {client.email || "Sem e-mail"}
                        </p>
                        {client.userDeleted ? (
                          <Badge variant="muted" className="mt-1">
                            Conta excluída
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatPhone(client.phone)}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {region ? formatRegion(region) : "-"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">{formatDate(client.createdAt)}</TableCell>
                      <TableCell className="cell-actions">
                        <Button asChild variant="ghost" size="sm">
                          <Link href={`/clientes/${client.id}`}>
                            Abrir <ChevronRight />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <Pagination {...pagination.paginationProps} itemLabel="clientes" />
        </CardContent>
      </Card>
    </>
  );
}
